import React from "react";
import WebProfitSpinner from "../system/WebProfitSpinner";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Switch,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ResponsiveOperationalTable from "./ResponsiveOperationalTable";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { affiliatesApi } from "../../api/affiliates";
import DashboardLoadingState from "./DashboardLoadingState";

const BRAND = {
  orange: "#F35A3A",
  dark: "var(--wp-panel-text-primary, #19242F)",
  page: "var(--wp-panel-bg, #F6F7FB)",
  border: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)",
  muted: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)",
};

function percent(value, maximumFractionDigits = 2) {
  return `${new Intl.NumberFormat("es-ES", { maximumFractionDigits }).format(Number(value || 0))}%`;
}

function moneyList(values = {}) {
  const entries = Object.entries(values).filter(([, value]) => Number(value || 0) > 0);
  if (!entries.length) {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "ARS",
      maximumFractionDigits: 0,
    }).format(0);
  }
  return entries
    .map(([currency, amount]) => new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency,
      maximumFractionDigits: currency === "ARS" ? 0 : 2,
    }).format(Number(amount || 0)))
    .join(" + ");
}

function toLocalDateTimeInput(value = new Date(Date.now() + 60_000)) {
  const date = value instanceof Date ? value : new Date(value);
  const validDate = Number.isNaN(date.getTime()) ? new Date(Date.now() + 60_000) : date;
  const offset = validDate.getTimezoneOffset() * 60_000;
  return new Date(validDate.getTime() - offset).toISOString().slice(0, 16);
}

function dateTimeLabel(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-ES", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function MiniMetric({ icon, label, value }) {
  return (
    <Box sx={{ p: 2, borderRadius: 3, border: `1px solid ${BRAND.border}`, height: "100%" }}>
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Avatar
          variant="rounded"
          sx={{ width: 38, height: 38, bgcolor: "rgba(243,90,58,.10)", color: BRAND.orange }}
        >
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ fontWeight: 900, color: BRAND.muted }}>
            {label}
          </Typography>
          <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>{value}</Typography>
        </Box>
      </Stack>
    </Box>
  );
}

function buildPreview(pool, decay, count = 8) {
  const rows = [];
  let previousEntitlement = null;

  for (let depth = 1; depth <= count; depth += 1) {
    const relativeEntitlement = 100 * ((decay / 100) ** (depth - 1));
    const effectiveEntitlement = pool * (relativeEntitlement / 100);
    const sponsorDifference = previousEntitlement === null
      ? 0
      : Math.max(0, previousEntitlement - effectiveEntitlement);

    rows.push({
      depth,
      relativeEntitlement,
      effectiveEntitlement,
      sponsorDifference,
    });
    previousEntitlement = effectiveEntitlement;
  }

  return rows;
}

export default function AffiliateProgramAdminCard() {
  const [data, setData] = React.useState(null);
  const [form, setForm] = React.useState({
    enabled: true,
    affiliatePoolPercent: 100,
    depthDecayPercent: 70,
    effectiveFrom: toLocalDateTimeInput(),
  });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");

  const applyResponse = React.useCallback((response) => {
    const overview = response?.affiliateProgram || null;
    const program = overview?.program || {};
    const policy = program.currentPolicy || {};

    setData(overview);
    setForm({
      enabled: program.enabled !== false,
      affiliatePoolPercent: Number(policy.affiliatePoolPercent ?? 100),
      depthDecayPercent: Number(policy.depthDecayPercent ?? 70),
      effectiveFrom: toLocalDateTimeInput(),
    });
  }, []);

  React.useEffect(() => {
    let active = true;
    affiliatesApi.getAdminOverview()
      .then((response) => {
        if (active) applyResponse(response);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message || "No se pudo cargar el programa de referidos.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [applyResponse]);

  const affiliatePoolPercent = Number(form.affiliatePoolPercent);
  const depthDecayPercent = Number(form.depthDecayPercent);
  const effectiveFromDate = new Date(form.effectiveFrom);
  const invalid =
    !Number.isFinite(affiliatePoolPercent) ||
    affiliatePoolPercent < 0 ||
    affiliatePoolPercent > 100 ||
    !Number.isFinite(depthDecayPercent) ||
    depthDecayPercent <= 0 ||
    depthDecayPercent > 100 ||
    Number.isNaN(effectiveFromDate.getTime());
  const webProfitPercent = Number.isFinite(affiliatePoolPercent)
    ? Math.max(0, 100 - affiliatePoolPercent)
    : 0;
  const preview = invalid ? [] : buildPreview(affiliatePoolPercent, depthDecayPercent);

  const save = async () => {
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const response = await affiliatesApi.updateAdminSettings({
        enabled: form.enabled,
        affiliatePoolPercent,
        depthDecayPercent,
        effectiveFrom: effectiveFromDate.toISOString(),
      });
      applyResponse(response);
      setMessage(response?.message || "Política del programa actualizada.");
    } catch (requestError) {
      setError(requestError.message || "No se pudo guardar la nueva política.");
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) {
    return <DashboardLoadingState label="Cargando programa de referidos…" minHeight={360} />;
  }

  const metrics = data?.metrics || {};
  const program = data?.program || {};
  const currentPolicy = program.currentPolicy || {};
  const upcomingPolicies = program.upcomingPolicies || [];
  const policyHistory = program.policyHistory || [];

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${BRAND.border}`,
        boxShadow: "0 14px 34px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.05)",
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3 }, "&:last-child": { pb: { xs: 2.5, md: 3 } } }}>
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ md: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1.4} alignItems="center">
              <Avatar
                variant="rounded"
                sx={{ bgcolor: "rgba(243,90,58,.10)", color: BRAND.orange }}
              >
                <AccountTreeRoundedIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                  Programa dinámico de referidos
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.35, color: BRAND.muted }}>
                  Red recursiva, reparto diferencial y políticas versionadas por fecha.
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={form.enabled ? "Programa activo" : "Programa pausado"}
                color={form.enabled ? "success" : "default"}
                sx={{ fontWeight: 900 }}
              />
              {loading ? <WebProfitSpinner size={22} /> : null}
            </Stack>
          </Stack>

          {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}
          {message ? <Alert severity="success" onClose={() => setMessage("")}>{message}</Alert> : null}

          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <MiniMetric icon={<GroupsRoundedIcon />} label="CUENTAS EN REDES" value={metrics.referredUsers || 0} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MiniMetric icon={<AccountTreeRoundedIcon />} label="AFILIADOS CON RED" value={metrics.activeAffiliates || 0} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MiniMetric icon={<PaidRoundedIcon />} label="COMISIONES REGISTRADAS" value={moneyList(metrics.generatedByCurrency)} />
            </Grid>
          </Grid>

          <Paper
            elevation={0}
            sx={{ p: 2.25, borderRadius: 3, bgcolor: BRAND.page, border: `1px solid ${BRAND.border}` }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" sx={{ fontWeight: 900, color: BRAND.muted }}>
                  POLÍTICA VIGENTE
                </Typography>
                <Typography sx={{ mt: 0.35, fontWeight: 950, color: BRAND.dark }}>
                  Versión {currentPolicy.version || "—"}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.35, color: BRAND.muted }}>
                  Desde {dateTimeLabel(currentPolicy.effectiveFrom)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" sx={{ fontWeight: 900, color: BRAND.muted }}>
                  PARTICIPACIÓN DE LA RED
                </Typography>
                <Typography sx={{ mt: 0.35, fontWeight: 950, color: BRAND.orange }}>
                  {percent(currentPolicy.affiliatePoolPercent)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.35, color: BRAND.muted }}>
                  Máximo distribuible por cada pago.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" sx={{ fontWeight: 900, color: BRAND.muted }}>
                  MARGEN DE WEBPROFIT
                </Typography>
                <Typography sx={{ mt: 0.35, fontWeight: 950, color: BRAND.dark }}>
                  {percent(currentPolicy.webProfitPercent)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.35, color: BRAND.muted }}>
                  Queda fuera de la bolsa de afiliados.
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          <Divider />

          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-start" }}>
              <Box sx={{ minWidth: { md: 230 }, pt: 0.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Switch
                    checked={form.enabled}
                    onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))}
                  />
                  <Box>
                    <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Generar comisiones</Typography>
                    <Typography variant="caption" sx={{ color: BRAND.muted }}>
                      Pausar no rompe la red ni el historial.
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <TextField
                label="Bolsa total para la red"
                type="number"
                value={form.affiliatePoolPercent}
                onChange={(event) => setForm((current) => ({ ...current, affiliatePoolPercent: event.target.value }))}
                error={affiliatePoolPercent < 0 || affiliatePoolPercent > 100}
                helperText={`WebProfit conservará ${percent(webProfitPercent)}`}
                inputProps={{ min: 0, max: 100, step: 0.5 }}
                InputProps={{ endAdornment: <Typography sx={{ fontWeight: 900 }}>%</Typography> }}
                sx={{ flex: 1 }}
              />

              <TextField
                label="Retención por profundidad"
                type="number"
                value={form.depthDecayPercent}
                onChange={(event) => setForm((current) => ({ ...current, depthDecayPercent: event.target.value }))}
                error={depthDecayPercent <= 0 || depthDecayPercent > 100}
                helperText="Cada nuevo escalón conserva esta proporción"
                inputProps={{ min: 0.01, max: 100, step: 0.5 }}
                InputProps={{ endAdornment: <Typography sx={{ fontWeight: 900 }}>%</Typography> }}
                sx={{ flex: 1 }}
              />

              <TextField
                label="Aplicar desde"
                type="datetime-local"
                value={form.effectiveFrom}
                onChange={(event) => setForm((current) => ({ ...current, effectiveFrom: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                helperText="Puede comenzar ahora o quedar programada"
                sx={{ flex: 1 }}
              />
            </Stack>

            <Button
              variant="contained"
              disabled={loading || saving || invalid}
              onClick={save}
              startIcon={saving ? <WebProfitSpinner size={16} color="inherit" /> : <SaveRoundedIcon />}
              sx={{
                alignSelf: { xs: "stretch", md: "flex-end" },
                minWidth: 220,
                borderRadius: 999,
                bgcolor: BRAND.orange,
                textTransform: "none",
                fontWeight: 950,
                "&:hover": { bgcolor: "#DE4D30" },
              }}
            >
              Guardar nueva versión
            </Button>
          </Stack>

          {invalid ? (
            <Alert severity="warning">
              La bolsa debe estar entre 0% y 100%, la retención debe ser mayor que 0% y no superar 100%, y la fecha debe ser válida.
            </Alert>
          ) : (
            <Alert severity="info">
              Con esta configuración, el afiliado raíz puede recibir hasta {percent(affiliatePoolPercent)} por sus ventas directas. Cada escalón inferior conserva {percent(depthDecayPercent)} del derecho anterior y los referentes superiores cobran solamente las diferencias, sin superar nunca la bolsa total.
            </Alert>
          )}

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 950, color: BRAND.dark }}>
              Simulación automática de la curva
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.4, color: BRAND.muted }}>
              Esta tabla es una vista previa. Ningún porcentaje se guarda manualmente en los usuarios.
            </Typography>
          </Box>

          <TableContainer sx={{ borderRadius: 3, border: `1px solid ${BRAND.border}` }}>
            <ResponsiveOperationalTable mobileMode="scroll" size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: BRAND.page }}>
                  <TableCell>Posición interna</TableCell>
                  <TableCell align="right">Derecho relativo</TableCell>
                  <TableCell align="right">Comisión directa sobre el pago</TableCell>
                  <TableCell align="right">Diferencia para referente inmediato</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.map((row) => (
                  <TableRow key={row.depth}>
                    <TableCell>
                      <Chip size="small" label={`Profundidad ${row.depth}`} sx={{ fontWeight: 900 }} />
                    </TableCell>
                    <TableCell align="right">{percent(row.relativeEntitlement, 4)}</TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                        {percent(row.effectiveEntitlement, 4)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{row.depth === 1 ? "—" : percent(row.sponsorDifference, 4)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ResponsiveOperationalTable>
          </TableContainer>

          {upcomingPolicies.length ? (
            <Alert severity="warning">
              Hay {upcomingPolicies.length} política{upcomingPolicies.length === 1 ? "" : "s"} programada{upcomingPolicies.length === 1 ? "" : "s"}. La próxima comienza el {dateTimeLabel(upcomingPolicies[0]?.effectiveFrom)}.
            </Alert>
          ) : null}

          {policyHistory.length ? (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 950, color: BRAND.dark }}>
                Últimas versiones
              </Typography>
              <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {policyHistory.slice(0, 6).map((policy) => (
                  <Chip
                    key={`${policy.version}-${policy.effectiveFrom}`}
                    variant={policy.version === currentPolicy.version ? "filled" : "outlined"}
                    color={policy.version === currentPolicy.version ? "primary" : "default"}
                    label={`v${policy.version} · Red ${percent(policy.affiliatePoolPercent)} · ${dateTimeLabel(policy.effectiveFrom)}`}
                  />
                ))}
              </Stack>
            </Box>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}
